import { authFetch } from '../../../api/csrf';
import { get, post, put, patch, del } from '../../apiClient';
import { API_ENDPOINTS, STORAGE_KEYS } from '../../config';

const leadService = {
  /**
   * Create a new lead
   */
  createLead: async (leadData) => {
    try {
      const response = await post(API_ENDPOINTS.CRM.LEADS, leadData);

      if (response && response.responseCode === 201) {
        return {
          success: true,
          lead: response.data
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to create lead'
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to create lead'
      };
    }
  },

  /**
   * Get all leads with filters
   */
  getAllLeads: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();

      if (filters.search) queryParams.append('search', filters.search);
      if (filters.source && filters.source !== 'all') queryParams.append('source', filters.source);
      if (filters.stage && filters.stage !== 'all') queryParams.append('stage', filters.stage);
      if (filters.scoreLabel && filters.scoreLabel !== 'all') queryParams.append('scoreLabel', filters.scoreLabel);
      if (filters.assignedTo && filters.assignedTo !== 'all') queryParams.append('assignedTo', filters.assignedTo);
      if (filters.managerId) queryParams.append('managerId', filters.managerId);
      if (filters.salesExecutiveId) queryParams.append('salesExecutiveId', filters.salesExecutiveId);
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);
      if (filters.range) queryParams.append('range', filters.range);
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.limit) queryParams.append('limit', filters.limit);
      if (filters.sortBy) queryParams.append('sortBy', filters.sortBy);
      if (filters.sortOrder) queryParams.append('sortOrder', filters.sortOrder);
      if (filters.region && filters.region !== 'all') queryParams.append('region', filters.region);
      if (filters.companySource && filters.companySource !== 'all') queryParams.append('companySource', filters.companySource);
      if (filters.country && filters.country !== 'all') queryParams.append('country', filters.country);
      if (filters.hasPendingVideoVerification) queryParams.append('hasPendingVideoVerification', filters.hasPendingVideoVerification);
      if (filters.videoVerificationStatus && filters.videoVerificationStatus !== 'all') queryParams.append('videoVerificationStatus', filters.videoVerificationStatus);
      if (filters.leadVideoStatus && filters.leadVideoStatus !== 'all') queryParams.append('leadVideoStatus', filters.leadVideoStatus);
      if (filters.leadOrderStatus && filters.leadOrderStatus !== 'all') queryParams.append('leadOrderStatus', filters.leadOrderStatus);
      if (filters.leadCallStatus && filters.leadCallStatus !== 'all') queryParams.append('leadCallStatus', filters.leadCallStatus);
      if (filters.leadInterest && filters.leadInterest !== 'all') queryParams.append('leadInterest', filters.leadInterest);
      if (filters.duplicateFilter) queryParams.append('duplicateFilter', filters.duplicateFilter);

      const url = queryParams.toString()
        ? `${API_ENDPOINTS.CRM.LEADS}?${queryParams.toString()}`
        : API_ENDPOINTS.CRM.LEADS;
      const response = await get(url);

      if (response && response.responseCode === 200) {
        // Handle both response structures:
        // 1. response.data is an array directly
        // 2. response.data.leads is an array (for paginated responses)
        const leads = Array.isArray(response.data)
          ? response.data
          : (response.data?.leads || []);

        return {
          success: true,
          leads: leads,
          pagination: response.data?.pagination || {}
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to retrieve leads',
          leads: []
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to retrieve leads',
        leads: []
      };
    }
  },

  /**
   * Get lead by ID
   */
  getLeadById: async (leadId) => {
    try {
      const response = await get(`${API_ENDPOINTS.CRM.LEADS}/${leadId}`);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          lead: response.data
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to retrieve lead'
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to retrieve lead'
      };
    }
  },

  /**
   * Export a single lead's full 360 detail as a backend-generated Excel
   * (.xlsx) workbook and trigger the browser download.
   */
  exportLeadDetail360: async (leadId, fallbackName = 'Lead') => {
    try {
      if (!leadId) {
        return { success: false, error: 'Lead ID is required' };
      }

      const blob = await get(`${API_ENDPOINTS.CRM.LEADS}/${leadId}/export`, { responseType: 'blob' });

      const sanitized = String(fallbackName || 'Lead')
        .replace(/[\\/:*?"<>|]/g, '')
        .replace(/\s+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 80) || 'Lead';
      const filename = `Lead_${sanitized}.xlsx`;

      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to export lead details'
      };
    }
  },

  /**
   * Update an existing lead
   */
  updateLead: async (leadId, leadData) => {
    try {
      const response = await put(`${API_ENDPOINTS.CRM.LEADS}/${leadId}`, leadData);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          lead: response.data,
          message: response.responseMessage || 'Lead updated successfully'
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to update lead'
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to update lead'
      };
    }
  },

  /**
   * Delete lead
   */
  deleteLead: async (leadId) => {
    try {
      const response = await del(`${API_ENDPOINTS.CRM.LEADS}/${leadId}`);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          message: response.responseMessage || 'Lead deleted successfully'
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to delete lead'
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to delete lead'
      };
    }
  },

  /**
   * Bulk delete leads
   */
  bulkDeleteLeads: async (leadIds) => {
    try {
      const response = await post(`${API_ENDPOINTS.CRM.LEADS}/bulk-delete`, {
        leadIds
      });

      if (response && response.responseCode === 200) {
        return {
          success: true,
          message: response.responseMessage || 'Leads deleted successfully',
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to delete leads'
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to delete leads'
      };
    }
  },

  /**
   * Bulk assign leads
   */
  bulkAssignLeads: async (leadIds, assigneeId) => {
    try {
      const response = await post(`${API_ENDPOINTS.CRM.LEADS}/bulk-assign`, {
        leadIds,
        assigneeId
      });

      if (response && response.responseCode === 200) {
        return {
          success: true,
          message: response.responseMessage || 'Leads assigned successfully',
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to assign leads'
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to assign leads'
      };
    }
  },

  /**
   * Get qualified leads ready for conversion (lightweight)
   */
  getQualifiedLeadsForConversion: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();

      if (filters.search) queryParams.append('search', filters.search);
      if (filters.source && filters.source !== 'all') queryParams.append('source', filters.source);
      if (filters.scoreLabel && filters.scoreLabel !== 'all') queryParams.append('scoreLabel', filters.scoreLabel);
      if (filters.region && filters.region !== 'all') queryParams.append('region', filters.region);
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.limit) queryParams.append('limit', filters.limit);
      if (filters.sortBy) queryParams.append('sortBy', filters.sortBy);
      if (filters.sortOrder) queryParams.append('sortOrder', filters.sortOrder);
      if (filters.companySource && filters.companySource !== 'all') queryParams.append('companySource', filters.companySource);
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);
      if (filters.country && filters.country !== 'all') queryParams.append('country', filters.country);

      const url = queryParams.toString()
        ? `${API_ENDPOINTS.CRM.LEADS}/qualified-for-conversion?${queryParams.toString()}`
        : `${API_ENDPOINTS.CRM.LEADS}/qualified-for-conversion`;
      const response = await get(url);

      if (response && response.responseCode === 200) {
        const leads = Array.isArray(response.data)
          ? response.data
          : (response.data?.leads || []);

        return {
          success: true,
          leads: leads,
          pagination: response.data?.pagination || {},
          totalQualifiedCount: response.data?.totalQualifiedCount
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to retrieve qualified leads',
          leads: []
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to retrieve qualified leads',
        leads: []
      };
    }
  },

  /**
   * Get leads with status 'converted'
   */
  getConvertedLeads: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();

      if (filters.search) queryParams.append('search', filters.search);
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.limit) queryParams.append('limit', filters.limit);
      if (filters.stage) queryParams.append('stage', filters.stage);

      const url = queryParams.toString()
        ? `${API_ENDPOINTS.CRM.LEADS}/converted?${queryParams.toString()}`
        : `${API_ENDPOINTS.CRM.LEADS}/converted`;

      const response = await get(url);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          leads: response.data?.leads || [],
          pagination: response.data?.pagination || {}
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to retrieve converted leads',
          leads: []
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to retrieve converted leads',
        leads: []
      };
    }
  },

  /**
   * Convert a qualified lead to an opportunity
   */
  convertToOpportunity: async (leadId, opportunityData = {}) => {
    try {
      const response = await post(
        `${API_ENDPOINTS.CRM.LEADS}/${leadId}/convert-to-opportunity`,
        opportunityData
      );

      if (response && response.responseCode === 201) {
        return {
          success: true,
          opportunity: response.data?.opportunity || response.data,
          lead: response.data?.lead,
          message: response.responseMessage || 'Lead converted to opportunity successfully'
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to convert lead to opportunity'
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to convert lead to opportunity'
      };
    }
  },

  /**
   * Get lead dashboard statistics
   */
  getLeadStatistics: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (filters.managerId) queryParams.append('managerId', filters.managerId);
      if (filters.salesExecutiveId) queryParams.append('salesExecutiveId', filters.salesExecutiveId);
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);
      if (filters.range) queryParams.append('range', filters.range);
      if (filters.sources && filters.sources.length > 0) {
        queryParams.append('sources', filters.sources.join(','));
      }

      const url = queryParams.toString()
        ? `${API_ENDPOINTS.CRM.LEAD_STATISTICS}?${queryParams.toString()}`
        : API_ENDPOINTS.CRM.LEAD_STATISTICS;

      const response = await get(url);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          statistics: response.data
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to retrieve lead statistics'
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to retrieve lead statistics'
      };
    }
  },

  /**
   * Initiate a call to a lead via RP Digital
   */
  initiateCall: async (leadId) => {
    try {
      const response = await post(`${API_ENDPOINTS.CRM.LEADS}/${leadId}/initiate-call`, {});

      if (response && response.responseCode === 200) {
        return {
          success: true,
          activity: response.data?.activity,
          callId: response.data?.callId,
          message: response.responseMessage || 'Call initiated successfully'
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to initiate call'
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.response?.data?.responseMessage || error.message || 'Failed to initiate call'
      };
    }
  },

  /**
   * Sync external leads from website APIs
   */
  syncExternalLeads: async () => {
    try {
      const response = await post(`${API_ENDPOINTS.CRM.LEADS}/sync-external`, {});

      if (response && response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage || 'External leads synced successfully'
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to sync external leads'
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.response?.data?.responseMessage || error.message || 'Failed to sync external leads'
      };
    }
  },

  /**
   * Get lead sources data for Lead Sources & Capture page
   */
  getLeadSourcesData: async () => {
    try {
      const response = await get(`${API_ENDPOINTS.CRM.LEADS}/sources`);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          sources: response.data || []
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to retrieve lead sources data',
          sources: []
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.response?.data?.responseMessage || error.message || 'Failed to retrieve lead sources data',
        sources: []
      };
    }
  },

  /**
   * Bulk upload leads from parsed CSV data
   * @param {Array} leads - Array of lead objects
   * @param {Object} options - Upload options (skipDuplicates, validateOnly)
   */
  bulkUploadLeads: async (leads, options = {}) => {
    try {
      const { skipDuplicates = true, validateOnly = false } = options;

      const response = await post(`${API_ENDPOINTS.CRM.LEADS}/bulk-upload`, {
        leads,
        skipDuplicates,
        validateOnly
      });

      if (response && (response.responseCode === 201 || response.responseCode === 200)) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage || 'Bulk upload completed'
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to upload leads',
          data: response.data
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.response?.data?.responseMessage || error.message || 'Failed to upload leads',
        data: error.response?.data?.data
      };
    }
  },

  /**
   * Validate bulk upload data without creating leads
   * @param {Array} leads - Array of lead objects to validate
   */
  validateBulkUpload: async (leads) => {
    try {
      const response = await post(`${API_ENDPOINTS.CRM.LEADS}/bulk-upload/validate`, {
        leads
      });

      if (response && response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          isValid: response.data?.isValid,
          errors: response.data?.errors || [],
          duplicates: response.data?.duplicates || []
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to validate leads',
          data: response.data
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.response?.data?.responseMessage || error.message || 'Failed to validate leads'
      };
    }
  },

  /**
   * Download bulk upload CSV template
   */
  downloadBulkUploadTemplate: async (filename = 'lead_bulk_upload_template.csv') => {
    try {      /*
       * fetch call to /bulk-upload/template
       * Note: The backend likely returns the file content (Blob) directly.
       */
      const response = await authFetch(`${API_ENDPOINTS.CRM.LEADS}/bulk-upload/template`, {
        credentials: 'include',
        method: 'GET',
        headers: {
                  }
      });

      if (!response.ok) {
        throw new Error('Failed to download template');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to download template'
      };
    }
  },

  /**
   * Parse CSV content to lead objects
   * @param {String} csvContent - Raw CSV content
   * @returns {Object} { success, leads, error }
   */
  parseCSV: (csvContent) => {
    try {
      const lines = csvContent.split(/\r?\n/).filter(line => line.trim());
      if (lines.length < 2) {
        return {
          success: false,
          error: 'CSV must contain a header row and at least one data row'
        };
      }

      // Parse header
      const headers = parseCSVLine(lines[0]);

      // Parse data rows
      const leads = [];
      for (let i = 1; i < lines.length; i++) {
        const values = parseCSVLine(lines[i]);
        if (values.length === 0 || values.every(v => !v.trim())) continue;

        const lead = {};
        headers.forEach((header, index) => {
          lead[header.trim()] = values[index]?.trim() || '';
        });
        leads.push(lead);
      }

      return { success: true, leads };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to parse CSV'
      };
    }
  },

  /**
   * Add a remark to a lead
   */
  addRemark: async (leadId, remark, file) => {
    try {
      const response = await post(`${API_ENDPOINTS.CRM.LEADS}/${leadId}/remark`, { remark, file });

      if (response && response.responseCode === 201) {
        return {
          success: true,
          remark: response.data
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to add remark'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to add remark'
      };
    }
  },

  /**
   * Get paginated remarks for a lead
   */
  getLeadRemarks: async (leadId, page = 1, limit = 10) => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('page', page);
      queryParams.append('limit', limit);

      const response = await get(`${API_ENDPOINTS.CRM.LEADS}/${leadId}/remarks?${queryParams.toString()}`);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          remarks: response.data?.remarks || [],
          pagination: response.data?.pagination || {}
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to retrieve remarks',
          remarks: []
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to retrieve remarks',
        remarks: []
      };
    }
  },

  /**
   * Approve a lead document video verification
   */
  approveVideoVerification: async (leadId, documentIndex) => {
    try {
      const response = await patch(
        `${API_ENDPOINTS.CRM.LEADS}/${leadId}/documents/${documentIndex}/approve-video`,
        {}
      );

      if (response && (response.responseCode === 200 || response.responseCode === 201)) {
        return {
          success: true,
          lead: response.data,
          message: 'Video verification approved successfully'
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to approve video verification'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to approve video verification'
      };
    }
  },

  /**
   * Reject a lead document video verification with optional reason
   */
  rejectVideoVerification: async (leadId, documentIndex, rejectionReason = '') => {
    try {
      const response = await patch(
        `${API_ENDPOINTS.CRM.LEADS}/${leadId}/documents/${documentIndex}/reject-video`,
        { rejectionReason }
      );

      if (response && (response.responseCode === 200 || response.responseCode === 201)) {
        return {
          success: true,
          lead: response.data,
          message: 'Video verification rejected successfully'
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to reject video verification'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to reject video verification'
      };
    }
  }
};

/**
 * Helper function to parse a CSV line handling quoted values
 */
const parseCSVLine = (line) => {
  const values = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      values.push(current);
      current = '';
    } else {
      current += char;
    }
  }

  values.push(current);
  return values;
};

export default leadService;
